import React from "react";
import { SEO } from "../components/SEO";
import { WebForm } from "../components/WebForm";
import { COMPANY_INFO } from "../data/company";

export function ContactPage() {
  return (
    <div className="min-h-screen flex flex-col pt-28 pb-16 bg-[#f3f4f6]">
      <SEO
        title="Kontakt - Kokholm Byg"
        description="Du kan komme i kontakt med os ved at ringe, skrive en mail eller udfylde formularen nedenfor, hvorefter vi vil kontakte dig hurtigst muligt."
      />

      {/* Hero Banner */}
      <section 
        style={{ marginTop: '-40px' }}
        className="bg-[#1e2329] text-white py-14 border-b border-[#2d343f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider bg-[#272d36] px-3 py-1 rounded border border-[#373f4b]">
              Uforpligtende samtale & Tilbud
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3 text-white">
              Kontakt Kokholm Byg
            </h1>
            <p className="text-gray-300 text-sm sm:text-base mt-2">
              Har du spørgsmål eller ønsker du et uforpligtende tilbud på dit næste byggeprojekt? Vi glæder os til at høre fra dig.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Contact Info & Map */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 shadow-sm space-y-6">
                <h2 className="text-2xl font-bold text-[#1e2329] pb-3 border-b border-gray-100">
                  Virksomhedsoplysninger
                </h2>

                <div className="space-y-4 text-sm text-gray-700">
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-lg shrink-0 mt-0.5">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-[#1e2329] text-base">{COMPANY_INFO.name}</p>
                      <p className="text-gray-500 text-xs mt-0.5">Murer- og byggefirma</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 border-t border-gray-100 pt-4">
                    <div className="w-10 h-10 rounded bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-lg shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e2329]">Adresse</p>
                      <p className="text-gray-600">{COMPANY_INFO.address}</p>
                      <p className="text-gray-600">{COMPANY_INFO.city}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 border-t border-gray-100 pt-4">
                    <div className="w-10 h-10 rounded bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-lg shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e2329]">Telefon</p>
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="nowrap-contact font-bold text-[#c06228] hover:underline text-base"
                      >
                        <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 border-t border-gray-100 pt-4">
                    <div className="w-10 h-10 rounded bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-lg shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e2329]">E-mail</p>
                      <a
                        href={`mailto:${COMPANY_INFO.email}`}
                        className="nowrap-contact font-bold text-[#c06228] hover:underline"
                      >
                        <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5 border-t border-gray-100 pt-4">
                    <div className="w-10 h-10 rounded bg-[#c06228]/10 text-[#c06228] flex items-center justify-center text-lg shrink-0 mt-0.5">
                      <i className="fa-solid fa-id-card" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1e2329]">CVR-nummer</p>
                      <p className="text-gray-600 font-mono text-sm">{COMPANY_INFO.cvr}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <a
                    href={COMPANY_INFO.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-[#272d36] hover:bg-[#323945] text-white py-3 px-4 rounded text-sm font-semibold transition-colors"
                  >
                    <i className="fa-brands fa-facebook-f text-blue-400" aria-hidden="true" />
                    <span>Følg os på Facebook</span>
                  </a>
                </div>
              </div>

              {/* Responsive Google Map Embed */}
              <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm overflow-hidden space-y-2">
                <p className="text-xs font-semibold text-gray-700 px-2 flex items-center gap-1.5">
                  <i className="fa-solid fa-map-pin text-[#c06228]" aria-hidden="true" />
                  <span>Find os i Holstebro</span>
                </p>
                <div className="w-full h-64 rounded overflow-hidden relative">
                  <iframe
                    title="Kokholm Byg placering på kort"
                    src="https://maps.google.com/maps?q=Tingagerparken%2011%2C%207500%20Holstebro&t=&z=14&ie=UTF8&iwloc=&output=embed"
                    className="w-full h-full border-0"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

            </div>

            {/* Right WebForm */}
            <div className="lg:col-span-7">
              <WebForm />
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
